import { API_BASE_URL } from '@config/config';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError
} from 'axios';
import AuthService from '@apis/AuthService';
import { attachAuthHeaders } from '@apis/authHeaders';
import LocalStorageService from '@services/LocalStorageService'
import SessionStorageService from '@services/SessionStorageService';
import LocalStorageKey from '@enums/LocalStorageKey';

// Operator-scoped requests: when set, the request interceptor omits the per-tab
// Masquerade header so a call resolves on the operator's own identity even
// mid-masquerade (see attachAuthHeaders / the masquerade picker).
declare module 'axios' {
  interface AxiosRequestConfig {
    skipMasquerade?: boolean;
  }
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  status?: number;
  /** When success is false, the raw error response body from the server (e.g. { code, username }). */
  errorData?: Record<string, unknown>;
}

/**
 * Clean query params
 * Remove undefined and empty values
 *
 * @param queryParams - The query params to clean
 * @returns The cleaned query params
 */
const cleanQueryParams = (queryParams: Record<string, any>) => {
  if (!queryParams) {
    return {};
  }
  return Object.entries(queryParams).reduce(
    (acc, [key, value]) => {
      if (value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>,
  );
};


const REFRESH_MAX_RETRIES = 2;
const REFRESH_BACKOFF_BASE_MS = 1000;

class ApiService {
  private apiClient: AxiosInstance;
  private isRefreshing: boolean;
  private isLoggingOut: boolean;
  private isRedirectingToLogin: boolean;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: any) => void;
  }>;

  constructor() {
    this.isRefreshing = false;
    this.isLoggingOut = false;
    this.isRedirectingToLogin = false;
    this.failedQueue = [];
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Response interceptor to handle errors globally
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => this.handleResponseError(error)
    );

    // Request interceptor: attach masquerade + per-tab workspace headers
    // (shared with raw-fetch SSE sites via attachAuthHeaders) and clean
    // query params.
    this.apiClient.interceptors.request.use(
      (config) => {
        attachAuthHeaders(config.headers as unknown as Record<string, string>, {
          skipMasquerade: config.skipMasquerade,
        });
        config.params = cleanQueryParams(config.params);
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  private async handleResponseError(
    error: AxiosError,
  ): Promise<AxiosResponse | void> {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // Don't attempt refresh if we're already logging out or if this is a logout request
    if (this.isLoggingOut || originalRequest.url?.includes('/auth/logout')) {
      return Promise.reject(error);
    }

    // If this is the refresh token endpoint failing, immediately logout
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      originalRequest.url?.includes('/auth/refresh')
    ) {
      this.isLoggingOut = true;
      AuthService.logout().finally(() => {
        this.isLoggingOut = false;
        this.clearLocalAuthState();
        this.redirectToLogin();
      });
      return Promise.reject(error);
    }

    // Only refresh and retry on 401 (e.g. expired token). Do not treat 403 (e.g. SUBSCRIPTION_REQUIRED) as auth failure.
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !this.isLoggingOut
    ) {
      if (this.isRefreshing) {
        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject });
        })
        .then(() => {
          return this.apiClient(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      this.isRefreshing = true;

      try {
        await this.refreshWithRetry();

        this.processQueue(null);

        const retryResponse = await this.apiClient(originalRequest);

        // Only logout if retry still returns 401 (token invalid). 403 (e.g. subscription) should not logout.
        if (retryResponse.status === 401) {
          this.isLoggingOut = true;
          AuthService.logout().finally(() => {
            this.isLoggingOut = false;
            this.clearLocalAuthState();
            this.redirectToLogin();
          });
        }

        return retryResponse;
      } catch (err) {
        this.processQueue(err as AxiosError);
        this.isLoggingOut = true;
        AuthService.logout().finally(() => {
          this.isLoggingOut = false;
          this.clearLocalAuthState();
          this.redirectToLogin();
        });
        return Promise.reject(err);
      } finally {
        this.isRefreshing = false;
      }
    }

    const parsedError = this.handleError(error);
    return Promise.reject(parsedError);
  }

    /**
   * Handle error responses and transform them
   */
    private handleError(error: any): any {
      if (error.response) {
        if (error.response.status === 401) {
          this.redirectToLogin();
        }
      }
      return error;
    }

  /**
   * Refresh the session for a request that bypasses axios — the chat send is a
   * raw `fetch`, so the interceptor above never sees its 401. Joins a refresh
   * already in flight rather than racing it with a second one. Rejects when the
   * refresh fails; the caller decides what a dead session means for it.
   */
  public async refreshSession(): Promise<void> {
    if (this.isRefreshing) {
      await new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
      return;
    }
    this.isRefreshing = true;
    try {
      await this.refreshWithRetry();
      this.processQueue(null);
    } catch (err) {
      this.processQueue(err as AxiosError);
      throw err;
    } finally {
      this.isRefreshing = false;
    }
  }

  private clearLocalAuthState(): void {
    LocalStorageService.removeItem(LocalStorageKey.USER_PROFILE);
    LocalStorageService.removeItem(LocalStorageKey.ACCESS_TOKEN_EXPIRES);
    LocalStorageService.removeItem(LocalStorageKey.WORKSPACE);
    // Masquerade lives in sessionStorage (per-tab isolation).
    SessionStorageService.removeItem(LocalStorageKey.MASQUERADE_USER);
    SessionStorageService.removeItem(LocalStorageKey.MASQUERADE_OPERATOR_EMAIL);
    sessionStorage.removeItem('workspace_reload_count');
  }

  private redirectToLogin(): void {
    if (this.isRedirectingToLogin) {
      return;
    }
    // Already on the login page: navigating to the same URL forces a full
    // reload, and if a stale authed request keeps 401ing here (e.g. a persisted
    // store rehydrating), that reload re-fires the request and we loop forever.
    // The isRedirectingToLogin flag can't catch this because each reload builds
    // a fresh ApiService with the flag reset.
    if (window.location.pathname.startsWith('/auth/login')) {
      return;
    }
    this.isRedirectingToLogin = true;
    window.location.href = '/auth/login';
  }

  /**
   * Transform Axios response into ApiResponse
   */
  private transformResponse<T>(response: AxiosResponse<T>): ApiResponse<T> {
    // Check if response contains access_expires and store it in local storage
    if (response.data && typeof response.data === 'object' && 'access_expires' in response.data) {
      const expiresAt = (response.data as any).access_expires;
      if (typeof expiresAt === 'number' && expiresAt > 0) {
        LocalStorageService.setItem(LocalStorageKey.ACCESS_TOKEN_EXPIRES, expiresAt);
      }
    }

    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  }

    /**
   *  Processes the queued requests that were waiting for the token to refresh.
   * Either reject or resolve all promises in the queue
   */
    private processQueue(
      error: AxiosError | null
    ): void {
      this.failedQueue.forEach((prom) => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve();
        }
      });
      this.failedQueue = [];
    }

  /**
   * Attempt to refresh the auth token with bounded retries and exponential backoff.
   * Prevents a single transient failure from immediately logging the user out.
   */
  private async refreshWithRetry(): Promise<void> {
    let lastError: Error | undefined;
    for (let attempt = 0; attempt <= REFRESH_MAX_RETRIES; attempt++) {
      try {
        await AuthService.refreshToken();
        return;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (attempt < REFRESH_MAX_RETRIES) {
          const delay = REFRESH_BACKOFF_BASE_MS * Math.pow(2, attempt);
          await new Promise((r) => setTimeout(r, delay));
        }
      }
    }
    throw lastError;
  }

  /**
   * Transforms an Axios error into an ApiResponse.
   * @param error - The error object caught from an Axios request.
   * @returns An ApiResponse with success set to false and an appropriate error message.
   */
  private transformErrorResponse<T>(error: unknown): ApiResponse<T> {
    let errorMessage = 'An unexpected error occurred. Please try again.';
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Server responded with a status code outside the 2xx range.
        // `data.error` may be a string OR a structured object
        // ({ message, error_code, ... }) — pull the human message out of the
        // object form so toasts never render a useless "[object Object]".
        const responseData: any = error.response.data;
        const nestedError = responseData?.error;
        errorMessage =
          responseData?.message ||
          (typeof nestedError === 'string' ? nestedError : nestedError?.message) ||
          responseData?.errors ||
          error.response.statusText ||
          errorMessage;
        return {
          success: false,
          error: errorMessage,
          status: error.response.status,
          errorData:
            typeof error.response.data === 'object' && error.response.data !== null
              ? (error.response.data as Record<string, unknown>)
              : undefined,
        };
      } else if (error.request) {
        // Request was made but no response received
        errorMessage =
          'No response received from the server. Please check your network connection.';
      } else {
        // Something happened in setting up the request
        errorMessage = error.message;
      }
    } else if (error instanceof Error) {
      // Non-Axios errors
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }

  /**
   * GET method
   */
  async get<T>(
    url: string,
    params?: Record<string, any>,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.apiClient.get<T>(url, {
        ...config,
        params,
      });
      return this.transformResponse(response);
    } catch (error) {
      return this.transformErrorResponse<T>(error);
    }
  }

  /**
   * POST method
   */
  async post<TRequest, TResponse>(
    url: string,
    data: TRequest,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<TResponse>> {
    try {
      const response = await this.apiClient.post<TResponse>(url, data, config);
      return this.transformResponse(response);
    } catch (error) {
      return this.transformErrorResponse<TResponse>(error);
    }
  }

  /**
   * POST FormData method
   */
  async postForm<FormData, TResponse>(
    url: string,
    data: FormData,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<TResponse>> {
    try {
      const response = await this.apiClient.post<TResponse>(url, data, {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      return this.transformResponse(response);
    } catch (error) {
      return this.transformErrorResponse<TResponse>(error);
    }
  }

  /**
   * PATCH method
   */
  async patch<TRequest, TResponse>(
    url: string,
    data: TRequest,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<TResponse>> {
    try {
      const response = await this.apiClient.patch<TResponse>(url, data, config);
      return this.transformResponse(response);
    } catch (error) {
      return this.transformErrorResponse<TResponse>(error);
    }
  }

  /**
   * PUT method
   */
  async put<TRequest, TResponse>(
    url: string,
    data: TRequest,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<TResponse>> {
    try {
      const response = await this.apiClient.put<TResponse>(url, data, config);
      return this.transformResponse(response);
    } catch (error) {
      return this.transformErrorResponse<TResponse>(error);
    }
  }

  /**
   * Put FormData method
   */
  async putForm<FormData, TResponse>(
    url: string,
    data: FormData,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<TResponse>> {
    try {
      const response = await this.apiClient.put<TResponse>(url, data, {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data',
        },
      });
      return this.transformResponse(response);
    } catch (error) {
      return this.transformErrorResponse<TResponse>(error);
    }
  }

  /**
   * DELETE method
   */
  async delete<T>(
    url: string,
    config: AxiosRequestConfig = {},
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.apiClient.delete<T>(url, config);
      return this.transformResponse(response);
    } catch (error) {
      return this.transformErrorResponse<T>(error);
    }
  }
}

export default new ApiService();
